import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { accessTokenApi } from "@/api/access-token";
import { ApiException } from "@/api/client";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/primitives/Button";
import { useSession } from "@/session/SessionProvider";

type AccessError = "invalid" | "rate_limit" | "network";

export default function AccessPage() {
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const { refresh } = useSession();
	const [error, setError] = useState<AccessError | null>(null);
	const started = useRef(false);

	useEffect(() => {
		const token = params.get("token")?.trim() ?? "";
		if (!token) {
			navigate("/login", { replace: true });
			return;
		}
		if (started.current) return;
		started.current = true;

		void (async () => {
			try {
				await accessTokenApi.exchange(token);
				await refresh();
				navigate("/app", { replace: true });
			} catch (err) {
				if (err instanceof ApiException) {
					if (err.status === 429) {
						setError("rate_limit");
						return;
					}
					if (err.status === 401) {
						setError("invalid");
						return;
					}
				}
				setError("network");
			}
		})();
	}, [params, navigate, refresh]);

	if (!params.get("token")?.trim()) return null;

	return (
		<AuthLayout
			title={error ? "Anmeldung fehlgeschlagen" : "Anmelden…"}
			subtitle={error ? undefined : "Einen Moment."}
		>
			{error === "invalid" ? (
				<p className="text-sm text-rose-500">
					Dieser Zugangslink ist ungültig oder wurde widerrufen.
				</p>
			) : null}
			{error === "rate_limit" ? (
				<p className="text-sm text-rose-500">
					Zu viele Versuche. Bitte in ein paar Minuten erneut versuchen.
				</p>
			) : null}
			{error === "network" ? (
				<p className="text-sm text-rose-500">
					Beim Anmelden ist etwas schiefgelaufen.
				</p>
			) : null}
			{error ? (
				<div className="mt-6 flex flex-col gap-3">
					<Button as="a" href="/login" variant="secondary">
						Zur Anmeldung
					</Button>
					<p className="text-sm text-fg-muted">
						<Link className="text-teal-600 hover:underline dark:text-teal-400" to="/">
							Startseite
						</Link>
					</p>
				</div>
			) : (
				<p className="text-sm text-fg-muted">Session wird hergestellt…</p>
			)}
		</AuthLayout>
	);
}
